import React, { useEffect, useState, useRef } from 'react';
import { Card, Spinner } from 'react-bootstrap';
import useAuth from '../../pages/useAuth';
import EmployeeHeader from '../../components/EmployeeHeader';

const PoojarisProfile = () => {
     useAuth();
     const baseUrl = process.env.REACT_APP_BASE_URL;
     const [attendanceData, setAttendanceData] = useState(null);
     const [loading, setLoading] = useState(true);
     const [statusCounts, setStatusCounts] = useState(null);
     const [employeeDetails, setEmployeeDetails] = useState(null);
     const [empLoading, setEmpLoading] = useState(true);

     const [currentEmployee, setCurrentEmployee] = useState({
          photo: 'https://randomuser.me/api/portraits/men/32.jpg',
          name: '',
          id: '',
          role: ''
     });

     const [showPopup, setShowPopup] = useState(false);
     const popupRef = useRef(null);
     const buttonRef = useRef(null);

     // 🧍‍♂️ Load current user info from session
     useEffect(() => {
          const userCode = sessionStorage.getItem('user_code');
          const userName = sessionStorage.getItem('user_name');
          const userRole = sessionStorage.getItem('user_role');
          setCurrentEmployee((prev) => ({
               ...prev,
               name: userName,
               id: userCode,
               role: userRole || 'User'
          }));
     }, []);

     // 📌 Fetch Employee Details API
     useEffect(() => {
          const userCode = sessionStorage.getItem('user_code');
          if (!userCode) return;

          const fetchEmployeeDetails = async () => {
               try {
                    setEmpLoading(true);
                    const token = sessionStorage.getItem('token') || localStorage.getItem('token');
                    const response = await fetch(
                         `${baseUrl}/getDetails?emp_code=${userCode}`,
                         {
                              method: 'GET',
                              headers: {
                                   'Content-Type': 'application/json',
                                   Authorization: `Bearer ${token}`,
                              },
                         }
                    );
                    const result = await response.json();

                    // API returns data object directly, not array
                    if (result.status && result.data) {
                         setEmployeeDetails(result.data);
                    }
               } catch (error) {
                    console.error('Error fetching employee details:', error);
               } finally {
                    setEmpLoading(false);
               }
          };

          fetchEmployeeDetails();
     }, [baseUrl]);

     // 📝 Fetch Attendance Data
     useEffect(() => {
          const fetchAttendance = async () => {
               try {
                    setLoading(true);
                    const token = sessionStorage.getItem('token') || localStorage.getItem('token');
                    const response = await fetch(`${baseUrl}/getUserAttendance`, {
                         method: 'GET',
                         headers: {
                              'Content-Type': 'application/json',
                              Authorization: `Bearer ${token}`,
                         },
                    });
                    const data = await response.json();
                    if (data.status) {
                         setAttendanceData(data.data);
                    }
               } catch (error) {
                    console.error('Error fetching attendance:', error);
               } finally {
                    setLoading(false);
               }
          };

          fetchAttendance();
     }, [baseUrl]);

     // 📊 Fetch Status Counts
     useEffect(() => {
          const fetchStatusCounts = async () => {
               try {
                    const token = sessionStorage.getItem('token') || localStorage.getItem('token');
                    const response = await fetch(`${baseUrl}/getStatus`, {
                         method: 'GET',
                         headers: {
                              'Content-Type': 'application/json',
                              Authorization: `Bearer ${token}`,
                         },
                    });

                    const result = await response.json();
                    if (result.status) {
                         setStatusCounts(result.data);
                    }
               } catch (error) {
                    console.error('Error fetching status counts:', error);
               }
          };

          fetchStatusCounts();
     }, [baseUrl]);

     // 📌 Close popup on outside click
     useEffect(() => {
          const handleClickOutside = (event) => {
               if (
                    popupRef.current &&
                    !popupRef.current.contains(event.target) &&
                    buttonRef.current &&
                    !buttonRef.current.contains(event.target)
               ) {
                    setShowPopup(false);
               }
          };

          document.addEventListener('mousedown', handleClickOutside);
          return () => {
               document.removeEventListener('mousedown', handleClickOutside);
          };
     }, []);

     // Helper function to format full name
     const getFullName = (details) => {
          if (!details) return '-';
          const firstName = details.fname || '';
          const middleName = details.mname || '';
          const lastName = details.lname || '';
          return `${firstName} ${middleName} ${lastName}`.trim().replace(/\s+/g, ' ');
     };

     // Helper function to format date
     const formatDate = (dateString) => {
          if (!dateString) return '-';
          const date = new Date(dateString);
          const day = String(date.getDate()).padStart(2, '0');
          const month = String(date.getMonth() + 1).padStart(2, '0');
          const year = date.getFullYear();
          return `${day}-${month}-${year}`;
     };

     // Helper function to get gender display
     const getGenderDisplay = (gender) => {
          if (!gender) return '-';
          return gender === 'M' ? 'Male' : gender === 'F' ? 'Female' : 'Other';
     };

     // Helper function to get status display
     const getStatusDisplay = (empStatus) => {
          if (!empStatus) return '-';
          return empStatus === 'A' ? 'Active' : empStatus === 'I' ? 'Inactive' : empStatus;
     };

     return (
          <>
               <EmployeeHeader
                    currentEmployee={currentEmployee}
                    showPopup={showPopup}
                    setShowPopup={setShowPopup}
                    popupRef={popupRef}
                    buttonRef={buttonRef}
               />

               <div
                    style={{
                         padding: '24px',
                         backgroundColor: '#f7f9fc',
                         minHeight: '100vh',
                    }}
               >
                    <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
                         {/* 🧾 Employee Details Card */}
                         <Card style={{ marginBottom: '24px', borderRadius: '12px', boxShadow: '0 2px 12px rgba(0,0,0,0.05)' }}>
                              <Card.Body>
                                   <h4 style={{ marginBottom: '20px', fontWeight: '600', color: '#2c3e50' }}>
                                        Employee Details
                                   </h4>

                                   {empLoading ? (
                                        <div style={{ textAlign: 'center', padding: '50px' }}>
                                             <Spinner animation="border" variant="primary" />
                                             <p style={{ marginTop: '16px', color: '#666' }}>Loading employee details...</p>
                                        </div>
                                   ) : employeeDetails ? (
                                        <div
                                             style={{
                                                  display: 'grid',
                                                  gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
                                                  gap: '16px',
                                             }}
                                        >
                                             {/* Personal Information */}
                                             <InfoRow
                                                  label="Employee Code"
                                                  value={employeeDetails.emp_code}
                                                  icon="👤"
                                             />
                                             <InfoRow
                                                  label="Full Name"
                                                  value={getFullName(employeeDetails)}
                                                  icon="📛"
                                             />
                                             <InfoRow
                                                  label="Gender"
                                                  value={getGenderDisplay(employeeDetails.gender)}
                                                  icon="⚥"
                                             />
                                             <InfoRow
                                                  label="Date of Birth"
                                                  value={formatDate(employeeDetails.dob)}
                                                  icon="🎂"
                                             />

                                             {/* Contact Information */}
                                             <InfoRow
                                                  label="Mobile"
                                                  value={employeeDetails.mobile}
                                                  icon="📱"
                                             />
                                             <InfoRow
                                                  label="Email"
                                                  value={employeeDetails.mail_id}
                                                  icon="📧"
                                             />

                                             {/* Work Information */}
                                             <InfoRow
                                                  label="Company"
                                                  value={employeeDetails.comp_name}
                                                  icon="🏢"
                                             />
                                             <InfoRow
                                                  label="Department"
                                                  value={employeeDetails.dept_name}
                                                  icon="🏛️"
                                             />
                                             <InfoRow
                                                  label="Designation"
                                                  value={employeeDetails.designation_name}
                                                  icon="💼"
                                             />
                                             <InfoRow
                                                  label="Organization Unit"
                                                  value={employeeDetails.ou_name}
                                                  icon="🏗️"
                                             />

                                             {/* Location Information */}
                                             <InfoRow
                                                  label="Location"
                                                  value={employeeDetails.location_name}
                                                  icon="📍"
                                             />
                                             <InfoRow
                                                  label="City"
                                                  value={employeeDetails.city}
                                                  icon="🌆"
                                             />
                                             <InfoRow
                                                  label="Region"
                                                  value={employeeDetails.region}
                                                  icon="🗺️"
                                             />
                                             <InfoRow
                                                  label="Country"
                                                  value={employeeDetails.country}
                                                  icon="🌍"
                                             />

                                             {/* Reporting Structure */}
                                             <InfoRow
                                                  label="Reporting Manager"
                                                  value={employeeDetails.report_mngr}
                                                  icon="👔"
                                             />
                                             <InfoRow
                                                  label="Functional Manager"
                                                  value={employeeDetails.function_mngr}
                                                  icon="👨‍💼"
                                             />

                                             {/* Employment Details */}
                                             <InfoRow
                                                  label="Date of Joining"
                                                  value={formatDate(employeeDetails.doj)}
                                                  icon="📅"
                                             />
                                             <InfoRow
                                                  label="Employment Status"
                                                  value={getStatusDisplay(employeeDetails.emp_status)}
                                                  icon="✓"
                                             />
                                             <InfoRow
                                                  label="Active Status"
                                                  value={employeeDetails.active}
                                                  icon="🟢"
                                             />
                                             <InfoRow
                                                  label="Validity"
                                                  value={formatDate(employeeDetails.validity)}
                                                  icon="⏰"
                                             />

                                             {/* Additional Information */}
                                             <InfoRow
                                                  label="Grade"
                                                  value={employeeDetails.grade}
                                                  icon="🎖️"
                                             />
                                             <InfoRow
                                                  label="Position"
                                                  value={employeeDetails.position}
                                                  icon="🎯"
                                             />
                                             <InfoRow
                                                  label="Cost Center"
                                                  value={employeeDetails.cost_center}
                                                  icon="💰"
                                             />
                                             <InfoRow
                                                  label="Pay Group"
                                                  value={employeeDetails.pay_group}
                                                  icon="💳"
                                             />

                                             {/* Bank Details */}
                                             <InfoRow
                                                  label="Bank Name"
                                                  value={employeeDetails.bank_name}
                                                  icon="🏦"
                                             />
                                             <InfoRow
                                                  label="Account Number"
                                                  value={employeeDetails.bank_acnum}
                                                  icon="🔢"
                                             />
                                             <InfoRow
                                                  label="IFSC Code"
                                                  value={employeeDetails.ifsc_code}
                                                  icon="🏧"
                                             />

                                             {/* Dates */}
                                             {employeeDetails.exit_date && (
                                                  <InfoRow
                                                       label="Exit Date"
                                                       value={formatDate(employeeDetails.exit_date)}
                                                       icon="🚪"
                                                  />
                                             )}
                                             <InfoRow
                                                  label="Created On"
                                                  value={formatDate(employeeDetails.created_on)}
                                                  icon="📝"
                                             />
                                             <InfoRow
                                                  label="Last Updated"
                                                  value={formatDate(employeeDetails.updated_at)}
                                                  icon="🔄"
                                             />
                                        </div>
                                   ) : (
                                        <div style={{ textAlign: 'center', padding: '50px' }}>
                                             <p style={{ fontSize: '18px', color: '#999' }}>😔 No details found.</p>
                                             <p style={{ fontSize: '14px', color: '#bbb' }}>Please contact HR department.</p>
                                        </div>
                                   )}
                              </Card.Body>
                         </Card>

                         {/* 📊 Attendance Summary Card (Optional - if you want to add it) */}
                         {statusCounts && (
                              <Card style={{ marginBottom: '24px', borderRadius: '12px', boxShadow: '0 2px 12px rgba(0,0,0,0.05)' }}>
                                   <Card.Body>
                                        <h4 style={{ marginBottom: '20px', fontWeight: '600', color: '#2c3e50' }}>
                                             Attendance Summary
                                        </h4>
                                        <div
                                             style={{
                                                  display: 'grid',
                                                  gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                                                  gap: '16px',
                                             }}
                                        >
                                             <StatCard label="Present" value={statusCounts.present || 0} color="#10b981" icon="✓" />
                                             <StatCard label="Absent" value={statusCounts.absent || 0} color="#ef4444" icon="✗" />
                                             <StatCard label="Half Day" value={statusCounts.halfday || 0} color="#f59e0b" icon="½" />
                                             <StatCard label="Week Off" value={statusCounts.weekoff || 0} color="#3b82f6" icon="📅" />
                                        </div>
                                   </Card.Body>
                              </Card>
                         )}
                    </div>
               </div>
          </>
     );
};

// Reusable info row component with icon
const InfoRow = ({ label, value, icon }) => (
     <div
          style={{
               background: '#fff',
               border: '1px solid #e5e7eb',
               padding: '14px 16px',
               borderRadius: '10px',
               display: 'flex',
               flexDirection: 'column',
               transition: 'all 0.2s',
               cursor: 'default',
          }}
          onMouseEnter={(e) => {
               e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.08)';
               e.currentTarget.style.borderColor = '#3b82f6';
          }}
          onMouseLeave={(e) => {
               e.currentTarget.style.boxShadow = 'none';
               e.currentTarget.style.borderColor = '#e5e7eb';
          }}
     >
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '6px' }}>
               {icon && <span style={{ fontSize: '14px' }}>{icon}</span>}
               <span style={{ fontSize: '12px', color: '#6b7280', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                    {label}
               </span>
          </div>
          <strong style={{ fontSize: '15px', color: '#1f2937', wordBreak: 'break-word' }}>
               {value || '-'}
          </strong>
     </div>
);

// Stat card component for attendance summary
const StatCard = ({ label, value, color, icon }) => (
     <div
          style={{
               background: '#fff',
               border: `2px solid ${color}`,
               padding: '20px',
               borderRadius: '10px',
               textAlign: 'center',
               transition: 'all 0.2s',
               cursor: 'default',
          }}
          onMouseEnter={(e) => {
               e.currentTarget.style.transform = 'translateY(-2px)';
               e.currentTarget.style.boxShadow = `0 8px 20px ${color}30`;
          }}
          onMouseLeave={(e) => {
               e.currentTarget.style.transform = 'translateY(0)';
               e.currentTarget.style.boxShadow = 'none';
          }}
     >
          <div style={{ fontSize: '32px', marginBottom: '8px' }}>{icon}</div>
          <div style={{ fontSize: '36px', fontWeight: '700', color: color, marginBottom: '8px' }}>
               {value}
          </div>
          <div style={{ fontSize: '14px', color: '#6b7280', fontWeight: '600', textTransform: 'uppercase' }}>
               {label}
          </div>
     </div>
);

export default PoojarisProfile;